import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Package, ShoppingCart, User as UserIcon, ArrowRight, PencilSimple, Camera, FloppyDisk, X } from '@phosphor-icons/react';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { authAPI } from '../lib/api';
import { logger } from '../lib/logger';

const ActionCard = ({ to, icon: Icon, title, description, testId }) => (
  <Link
    to={to}
    className="group bg-white border border-border p-6 hover:-translate-y-1 hover:shadow-lg transition-all"
    data-testid={testId}
  >
    <div className="w-14 h-14 rounded-full bg-orange-50 flex items-center justify-center mb-4">
      <Icon size={28} weight="duotone" className="text-accent" />
    </div>
    <h3 className="text-xl font-bold text-primary mb-1" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
      {title}
    </h3>
    <p className="text-sm text-text_muted mb-4">{description}</p>
    <div className="flex items-center text-accent text-sm font-medium group-hover:translate-x-1 transition-transform">
      <span>Open</span>
      <ArrowRight size={16} weight="bold" className="ml-1" />
    </div>
  </Link>
);

const DashboardPage = () => {
  const { user, updateUser } = useAuth();
  const displayName = ((user?.name || '').trim()) || 'there';
  const initial = displayName.charAt(0).toUpperCase() || 'U';

  const [editing, setEditing] = useState(false);
  const [draftName, setDraftName] = useState(displayName);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  const openEditor = () => {
    setDraftName((user?.name || '').trim());
    setEditing(true);
  };
  const cancelEdit = () => {
    setEditing(false);
    setDraftName((user?.name || '').trim());
  };

  const handleAvatarUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Please choose an image file');
      e.target.value = '';
      return;
    }
    setUploading(true);
    try {
      const res = await authAPI.uploadAvatar(file);
      // Server returns {user, picture_url, file_id} - trust the server's URL
      updateUser(res.data.user);
      toast.success('Profile picture updated');
    } catch (err) {
      logger.error('Avatar upload failed:', err);
      toast.error(err.response?.data?.detail || 'Could not update picture');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleSaveName = async () => {
    const name = (draftName || '').trim();
    if (!name) { toast.error('Name cannot be empty'); return; }
    if (name === (user?.name || '').trim()) { setEditing(false); return; }
    setSaving(true);
    try {
      const res = await authAPI.updateProfile({ name });
      updateUser(res.data);
      toast.success('Profile updated');
      setEditing(false);
    } catch (err) {
      logger.error('Profile update failed:', err);
      toast.error(err.response?.data?.detail || 'Could not update profile');
    } finally {
      setSaving(false);
    }
  };

  const AvatarEl = () => (
    user?.picture ? (
      <img src={user.picture} alt={displayName} className="w-20 h-20 rounded-full object-cover" />
    ) : (
      <div className="w-20 h-20 rounded-full bg-primary text-white flex items-center justify-center text-3xl font-black">
        {initial}
      </div>
    )
  );

  return (
    <div className="min-h-screen bg-background" data-testid="dashboard-page">
      <div className="container mx-auto px-4 py-10 max-w-5xl">
        {/* Welcome Section */}
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="text-center mb-10"
        >
          <div className="relative w-20 h-20 mx-auto mb-4 group" data-testid="dashboard-avatar">
            <AvatarEl />
            <label
              htmlFor="avatar-upload-input"
              className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer"
              title="Change picture"
              data-testid="avatar-upload-label"
            >
              <Camera size={22} className="text-white" />
              <input
                id="avatar-upload-input"
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleAvatarUpload}
                disabled={uploading}
                data-testid="avatar-upload-input"
              />
            </label>
            {uploading && (
              <div className="absolute inset-0 rounded-full flex items-center justify-center bg-black/50">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
              </div>
            )}
          </div>
          <h1
            className="text-3xl md:text-4xl font-black text-primary tracking-tight"
            style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            data-testid="welcome-heading"
          >
            Welcome, {displayName}!
          </h1>
        </motion.div>

        {/* Action Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <ActionCard to="/plans" icon={Package} title="Browse Plans" description="Explore our collection of building plans" testId="dashboard-browse-plans" />
          <ActionCard to="/orders" icon={ShoppingCart} title="My Orders" description="View your order history" testId="dashboard-my-orders" />
          <ActionCard to="/cart" icon={UserIcon} title="Shopping Cart" description="Review your selected plans" testId="dashboard-cart" />
        </div>

        {/* Account Information */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.1 }}
          className="bg-white border border-border p-6 md:p-8"
          data-testid="account-info-card"
        >
          <div className="flex items-center justify-between mb-6">
            <h2
              className="text-xl font-bold text-primary"
              style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}
            >
              Account Information
            </h2>
            {!editing ? (
              <button
                onClick={openEditor}
                className="flex items-center gap-1 text-sm text-accent hover:underline"
                data-testid="edit-profile-btn"
              >
                <PencilSimple size={16} />
                <span>Edit</span>
              </button>
            ) : (
              <button
                onClick={cancelEdit}
                className="flex items-center gap-1 text-sm text-text_muted hover:text-primary"
                data-testid="cancel-edit-btn"
              >
                <X size={16} />
                <span>Cancel</span>
              </button>
            )}
          </div>

          <dl className="divide-y divide-border">
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 gap-2">
              <dt className="text-sm font-bold text-primary sm:min-w-24">Name:</dt>
              {!editing ? (
                <dd className="text-sm text-text_muted" data-testid="account-name">{displayName}</dd>
              ) : (
                <dd className="flex-1 flex items-center gap-2 w-full">
                  <input
                    type="text"
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    className="flex-1 p-2 border border-border rounded text-sm focus:ring-2 focus:ring-accent focus:outline-none"
                    data-testid="profile-name-input"
                    autoFocus
                  />
                  <button
                    onClick={handleSaveName}
                    disabled={saving || !draftName.trim()}
                    className="flex items-center gap-1 bg-accent text-white px-3 py-2 rounded text-sm font-medium hover:bg-opacity-90 disabled:opacity-50"
                    data-testid="save-profile-btn"
                  >
                    <FloppyDisk size={16} weight="bold" />
                    <span>{saving ? 'Saving...' : 'Save'}</span>
                  </button>
                </dd>
              )}
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 gap-1">
              <dt className="text-sm font-bold text-primary sm:min-w-24">Email:</dt>
              <dd className="text-sm text-text_muted break-all" data-testid="account-email">{(user?.email || '').trim()}</dd>
            </div>
            <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center py-3 gap-1">
              <dt className="text-sm font-bold text-primary sm:min-w-24">Role:</dt>
              <dd className="text-sm text-text_muted capitalize" data-testid="account-role">{user?.role || 'Customer'}</dd>
            </div>
          </dl>
        </motion.div>
      </div>
    </div>
  );
};

export default DashboardPage;
